import React, { useEffect, useState, useContext } from 'react';
import { Card, CardBody, Col, Form } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { UserService } from "./service";
import { ErrorToast, SuccessToast } from '../../shared/toasters/toasters';
import DeleteModal from '../../Common/DeleteModal';
import Select from 'react-select';
import { useTranslation } from 'react-i18next';
import { EnumsContext } from "../../context/EnumsContext";

const UserList = () => {
  const { t } = useTranslation();
  const [list, setList] = useState([]);
  const [totalPages, setTotalPages] = useState(0);
  const [term, setTerm] = useState('');
  const [entriesPerPage, setEntriesPerPage] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);
  const { enums } = useContext(EnumsContext);
  const [role, setRole] = useState('');
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [userToDelete, setUserToDelete] = useState<number | null>(null);
  const navigate = useNavigate();

  const entriesPerPageOptions = [
    { label: '10', value: 10 },
    { label: '25', value: 25 },
    { label: '50', value: 50 },
    { label: '100', value: 100 },
    { label: '200', value: 200 },
  ];

  const handleTermChange = (e: any) => {
    setTerm(e.target.value);
    setCurrentPage(1);
  };

  const handleEntriesPerPageChange = (selectedOption: any) => {
    setEntriesPerPage(selectedOption ? selectedOption.value : 10);
    setCurrentPage(1);
  };

  const handleRoleChange = (selectedOption: any) => {
    setRole(selectedOption ? selectedOption.value : '');
    setCurrentPage(1);
  };

  const handlePageChange = (pageNumber: number) => {
    setCurrentPage(pageNumber);
  };

  const goToPreviousPage = () => {
    setCurrentPage((prevPage) => prevPage - 1);
  };

  const goToNextPage = () => {
    setCurrentPage((prevPage) => prevPage + 1);
  };

  const handleDeleteClick = (id: number) => {
    setUserToDelete(id);
    setShowDeleteModal(true);
  };

  const handleDeleteConfirm = async () => {
    if (userToDelete !== null) {
      try {
        await UserService.delete(userToDelete);
        SuccessToast(t('user.successDelete'));
        setShowDeleteModal(false);
        setUserToDelete(null);
        fetchData();
      } catch (error) {
        ErrorToast(error);
      }
    }
  };

  const fetchData = async () => {
    try {
      const response = await UserService.getAll({
        page: currentPage,
        perPage: entriesPerPage,
        term,
        ...(role && { role }),
      });
      const data = response.data;
      setList(data);
      setTotalPages(response.totalPages);
    } catch (error) {
      ErrorToast(error);
    }
  };

  useEffect(() => {
    fetchData();
  }, [currentPage, entriesPerPage, term, role]);

  return (
    <React.Fragment>
      <Col>
        <Card>
          <CardBody className="card-body">
            <div className='text-end'>
              <button className='btn btn-primary text-end' onClick={() => navigate('/users/create')}><i className='ti ti-plus f-18'></i> {t('user.add')}</button>
            </div>

            <div className="d-sm-flex align-items-center mb-3">
              <ul className="list-inline me-auto my-1">
                <li className="list-inline-item">
                  <Select
                    placeholder={t('user.entriesPerPage')}
                    options={entriesPerPageOptions}
                    onChange={handleEntriesPerPageChange}
                    defaultValue={entriesPerPageOptions.find(option => option.value === entriesPerPage)}
                    classNamePrefix="react-select"
                  />
                </li>
                <span> {t('user.entriesPerPage')}</span>
              </ul>
              <ul className="list-inline ms-auto my-1 mt-3">
                <li className="list-inline-item z-3" style={{ minWidth: '150px' }}>
                  <Select
                    placeholder={t('user.roles')}
                    options={
                      enums?.userRole?.map((r: any) => ({
                        value: r,
                        label: t(`enums.userRole.${r}`),
                      })) || []
                    }
                    onChange={handleRoleChange}
                    isClearable
                    classNamePrefix="react-select"
                  />
                </li>
                <li className="list-inline-item">
                  <form className="">
                    <input
                      className='form-control'
                      type="search"
                      placeholder={t('user.searchPlaceholder')}
                      onChange={handleTermChange}
                    />
                  </form>
                </li>
              </ul>
            </div>
            <div className="table-responsive">
              <table className="table table-hover" id="pc-dt-simple-1">
                <thead>
                  <tr>
                    <th>{t('user.id')}</th>
                    <th>{t('user.firstName')}</th>
                    <th>{t('user.lastName')}</th>
                    <th>{t('user.email')}</th>
                    <th>{t('user.active')}</th>
                    <th>{t('general.isEmailVerified')}</th>
                    <th className="text-end">{t('user.actions')}</th>
                  </tr>
                </thead>
                <tbody>
                  {list.map((item: any, index: number) => (
                    <tr key={index}>
                      <td>{item.id}</td>
                      <td>{item.firstName}</td>
                      <td>{item.lastName}</td>
                      <td>{item.email}</td>
                      <td>{item?.isActive ? t('user.active') : t('user.inactive')}</td>
                      <td>{item?.isEmailVerified ? t('common.yes') : t('common.no')}</td>
                      <td className="text-end">
                        <ul className="list-inline mb-0">
                          <li className="list-inline-item" onClick={() => navigate(`/users/edit/${item.id}`)}>
                            <a href="#" className="avtar avtar-s btn-link-success btn-pc-default">
                              <i className="ti ti-edit f-20"></i>
                            </a>
                          </li>
                          <li className="list-inline-item">
                            <span
                              role="button"
                              className="avtar avtar-s btn-link-danger btn-pc-default"
                              onClick={() => handleDeleteClick(item.id)}
                            >
                              <i className="ti ti-trash f-20"></i>
                            </span>
                          </li>
                        </ul>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <ul className="pagination justify-content-end me-3 mt-2 mb-0">
              <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                <button className="page-link" onClick={goToPreviousPage}>
                  {t('user.previous')}
                </button>
              </li>
              {Array.from({ length: totalPages }, (_, index) => (
                <li key={index} className={`page-item ${currentPage === index + 1 ? 'active' : ''}`}>
                  <button className="page-link" onClick={() => handlePageChange(index + 1)}>
                    {index + 1}
                  </button>
                </li>
              ))}
              <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                <button className="page-link" onClick={goToNextPage}>
                  {t('user.next')}
                </button>
              </li>
            </ul>
          </CardBody>
        </Card>
      </Col>

      <DeleteModal
        show={showDeleteModal}
        handleClose={() => setShowDeleteModal(false)}
        handleDeleteId={handleDeleteConfirm}
        name={t('user.name')}
      />
    </React.Fragment>
  );
};

export default UserList;